import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography, alpha, useTheme } from '@mui/material';
import { CheckCircleOutline, CloudUploadOutlined, Download, ErrorOutline } from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { useSnackbar } from 'notistack';
import { bulkResultService, BulkMarksScope } from '../services/bulkResultService';
import { examService } from '../services/examService';
import { sessionsApi } from '../services/sessions/sessionsApi';
import { classesApi } from '../services/sessions/classesApi';
import { sectionInstancesApi } from '../services/sessions/sectionInstancesApi';
import { useSessionContext } from '../context/SessionContext';
import { buildSessionOptions } from '../utils/sessionOptions';
import { fmtClassName } from '../utils/pdf';
import type { Exam } from '../types';
import type { BulkMarksOutcome } from '../types/examination';
import type { SectionInstance, Session, SessionClass } from '../types/sessions';
import StepDialog from './common/StepDialog';
import SearchableSelect from './common/SearchableSelect';

const STEPS = ['Choose the exam', 'Upload the sheet', 'Done'];

/**
 * Marks by spreadsheet: pick the exam, download its sheet (each student with
 * their current marks), fill it in, and upload it back. Only cells that
 * changed are recorded; submitted papers and anything invalid are listed
 * by student and subject.
 */
/** `onSuccess` runs after each upload so the page behind can refresh; the dialog stays on its outcome. */
const BulkResultUpload: React.FC<{ open: boolean; onClose: () => void; onSuccess?: () => void }> = ({ open, onClose, onSuccess }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { activeSession } = useSessionContext();
  const [step, setStep] = useState(0);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [classes, setClasses] = useState<SessionClass[]>([]);
  const [sections, setSections] = useState<SectionInstance[]>([]);
  const [exams, setExams] = useState<Exam[]>([]);
  const [scope, setScope] = useState<BulkMarksScope>({ sessionId: '', classId: '', sectionInstanceId: null, examId: '' });
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<BulkMarksOutcome | null>(null);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setOutcome(null);
    setScope({ sessionId: activeSession?._id ?? '', classId: '', sectionInstanceId: null, examId: '' });
    // A teacher sees the classes they are class teacher of; others see all.
    Promise.all([sessionsApi.list(), classesApi.list({ relationship: 'class-teacher' })])
      .then(([s, c]) => { setSessions(s); setClasses(c); })
      .catch((err: any) => enqueueSnackbar(err?.message || 'Could not load the classes', { variant: 'error' }));
  }, [open, activeSession, enqueueSnackbar]);

  useEffect(() => {
    const { sessionId, classId } = scope;
    if (!sessionId || !classId) { setSections([]); setExams([]); return; }
    let cancelled = false;
    Promise.all([
      sectionInstancesApi.list({ sessionId, classId, relationship: 'class-teacher' }),
      examService.getExams({ sessionId, classId, status: 'published', limit: 100 }),
    ])
      .then(([s, e]) => { if (!cancelled) { setSections(s); setExams(e.data?.exams ?? []); } })
      .catch((err: any) => { if (!cancelled) enqueueSnackbar(err?.message || 'Could not load the exams', { variant: 'error' }); });
    return () => { cancelled = true; };
  }, [scope.sessionId, scope.classId, enqueueSnackbar]); // eslint-disable-line react-hooks/exhaustive-deps

  const sessionOptions = useMemo(() => buildSessionOptions(sessions, theme), [sessions, theme]);
  const classOptions = useMemo(() => classes.map(c => ({ value: c._id, label: fmtClassName(c.name) })), [classes]);
  const sectionOptions = useMemo(() => sections.map(s => ({ value: s._id, label: s.name })), [sections]);
  const examOptions = useMemo(() => exams.map(e => ({ value: e._id, label: e.name, secondary: `${e.subjects.length} subjects` })), [exams]);
  const exam = exams.find(e => e._id === scope.examId);
  const ready = !!(scope.sessionId && scope.classId && scope.examId);

  const downloadTemplate = async () => {
    setBusy(true);
    try {
      await bulkResultService.downloadTemplate(scope);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not download the sheet', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file: File) => {
    setBusy(true);
    try {
      const result = await bulkResultService.upload(file, scope);
      setOutcome(result);
      setStep(2);
      onSuccess?.();
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || err?.message || 'Could not upload the sheet', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: files => files[0] && upload(files[0]),
    accept: { 'text/csv': ['.csv'], 'application/vnd.ms-excel': ['.csv'] },
    maxFiles: 1,
    disabled: busy,
  });

  const footer = (
    <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ px: 3, py: 2, width: '100%' }}>
      {step === 1 && <Button color="inherit" onClick={() => setStep(0)} disabled={busy}>Back</Button>}
      {step === 0 && <Button variant="contained" disableElevation disabled={!ready} onClick={() => setStep(1)}>Next</Button>}
      {step === 2 && <Button onClick={() => { setOutcome(null); setStep(1); }}>Upload another</Button>}
      {step === 2 && <Button variant="contained" disableElevation onClick={onClose}>Done</Button>}
    </Stack>
  );

  return (
    <StepDialog
      open={open}
      onClose={onClose}
      closeDisabled={busy}
      title="Upload marks"
      subtitle={exam ? `${exam.name} · ${fmtClassName(classes.find(c => c._id === scope.classId)?.name ?? '')}` : 'Fill in a spreadsheet and upload it back'}
      steps={STEPS}
      activeStep={step}
      maxWidth="sm"
      footer={footer}
    >
      {step === 0 && (
        <Stack spacing={2} sx={{ py: 1 }}>
          <SearchableSelect label="Session" value={scope.sessionId} onChange={v => setScope({ sessionId: v, classId: '', sectionInstanceId: null, examId: '' })} options={sessionOptions} clearable={false} />
          <SearchableSelect label="Class" value={scope.classId} onChange={v => setScope(s => ({ ...s, classId: v, sectionInstanceId: null, examId: '' }))} options={classOptions} />
          {sectionOptions.length > 0 && (
            <SearchableSelect label="Section" placeholder="All sections" value={scope.sectionInstanceId ?? ''} onChange={v => setScope(s => ({ ...s, sectionInstanceId: v || null }))} options={sectionOptions} />
          )}
          <SearchableSelect
            label="Exam"
            placeholder={scope.classId ? (examOptions.length ? 'Choose an exam' : 'No published exams') : 'Pick a class first'}
            value={scope.examId}
            onChange={v => setScope(s => ({ ...s, examId: v }))}
            options={examOptions}
            disabled={!scope.classId}
          />
        </Stack>
      )}

      {step === 1 && (
        <Stack spacing={2} sx={{ py: 1 }}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" fontWeight={700}>1. Download the sheet</Typography>
                <Typography variant="body2" color="text.secondary">
                  One row per student, with their current marks. Type marks, <b>A</b> for absent or <b>NA</b> for not appeared. Leave a cell blank to skip it.
                </Typography>
              </Box>
              <Button variant="outlined" startIcon={busy ? <CircularProgress size={14} /> : <Download />} onClick={downloadTemplate} disabled={busy}>Download</Button>
            </Stack>
          </Paper>
          <Box
            {...getRootProps()}
            sx={{
              p: 4, textAlign: 'center', borderRadius: 2, cursor: busy ? 'default' : 'pointer',
              border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
              bgcolor: isDragActive ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
              transition: 'all 120ms',
            }}
          >
            <input {...getInputProps()} />
            {busy ? <CircularProgress size={28} /> : <CloudUploadOutlined sx={{ fontSize: 40, color: 'primary.main' }} />}
            <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 1 }}>2. Upload it back</Typography>
            <Typography variant="body2" color="text.secondary">Drop the filled-in CSV here, or click to choose it.</Typography>
          </Box>
        </Stack>
      )}

      {step === 2 && outcome && (
        <Stack spacing={2} sx={{ py: 1 }}>
          <Alert severity={outcome.refused.length || outcome.problems.length ? 'warning' : 'success'} icon={outcome.refused.length || outcome.problems.length ? <ErrorOutline /> : <CheckCircleOutline />}>
            <b>{outcome.saved}</b> mark{outcome.saved === 1 ? '' : 's'} recorded{outcome.unchanged ? `, ${outcome.unchanged} unchanged` : ''}.
            {outcome.refused.length + outcome.problems.length > 0 && ` ${outcome.refused.length + outcome.problems.length} couldn't be.`}
          </Alert>
          {[...outcome.problems.map(p => ({ key: `r${p.row}${p.message}`, who: p.student ? `${p.student} · line ${p.row}` : `Line ${p.row}`, why: p.message })),
            ...outcome.refused.map(r => ({ key: `${r.studentId}${r.subjectId}`, who: `${r.studentName} · ${r.subjectName}`, why: r.reason }))]
            .map(item => (
              <Stack key={item.key} direction="row" spacing={2} sx={{ px: 1.5, py: 1, borderRadius: 1.5, bgcolor: alpha(theme.palette.warning.main, 0.06) }}>
                <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>{item.who}</Typography>
                <Typography variant="body2" color="text.secondary">{item.why}</Typography>
              </Stack>
            ))}
        </Stack>
      )}
    </StepDialog>
  );
};

export default BulkResultUpload;
